import React, { useState } from 'react';
import { formatPartyDisplay } from '../../utils/partyUtils';

// ─── Avatar ─────────────────────────────────────────────────────────────────

interface AvatarProps {
  name: string;
  photoUrl?: string | null;
  party?: string | null;
  size?: number;
  className?: string;
}

function getPartyTone(party: string | null | undefined) {
  const p = (party || '').toLowerCase();
  if (p.includes('democrat')) return { bg: 'bg-party-democrat-bg dark:bg-party-democrat-bg-dark', text: 'text-party-democrat-text dark:text-party-democrat-light' };
  if (p.includes('republican')) return { bg: 'bg-party-republican-bg dark:bg-party-republican-bg-dark', text: 'text-party-republican-text dark:text-party-republican-light' };
  if (p.includes('independ')) return { bg: 'bg-party-independent-bg dark:bg-party-independent-bg-dark', text: 'text-party-independent-text dark:text-party-independent-light' };
  return { bg: 'bg-primary-50 dark:bg-primary-dark-50', text: 'text-primary-800 dark:text-primary-dark-500' };
}

export const Avatar: React.FC<AvatarProps> = ({ name, photoUrl, party, size = 96, className = '' }) => {
  const [imgFailed, setImgFailed] = useState(false);

  const initials = (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(p => p[0])
    .join('')
    .toUpperCase();

  const tone = getPartyTone(party);

  if (photoUrl && !imgFailed) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className={`rounded-xl object-cover flex-shrink-0 ${className}`}
        style={{ width: size, height: size }}
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <div
      className={`rounded-xl flex items-center justify-center font-semibold flex-shrink-0 ${tone.bg} ${tone.text} ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.34, letterSpacing: '-0.02em' }}
    >
      {initials}
    </div>
  );
};

// ─── PartyChip ──────────────────────────────────────────────────────────────

interface PartyChipProps {
  party: string;
  size?: 'sm' | 'lg';
}

export const PartyChip: React.FC<PartyChipProps> = ({ party, size = 'sm' }) => {
  const p = (party || '').toLowerCase();
  const displayParty = formatPartyDisplay(party) || party;
  const cls = p.includes('democrat')
    ? 'bg-party-democrat-bg dark:bg-party-democrat-bg-dark text-party-democrat-text dark:text-party-democrat-light'
    : p.includes('republican')
    ? 'bg-party-republican-bg dark:bg-party-republican-bg-dark text-party-republican-text dark:text-party-republican-light'
    : p.includes('independ')
    ? 'bg-party-independent-bg dark:bg-party-independent-bg-dark text-party-independent-text dark:text-party-independent-light'
    : 'bg-neutral-100 dark:bg-background-dark-tertiary text-neutral-600 dark:text-text-dark-secondary';

  const padding = size === 'lg' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-medium ${cls} ${padding}`}>
      <PartyDot party={party} />
      {displayParty}
    </span>
  );
};

// ─── PartyDot ───────────────────────────────────────────────────────────────

interface PartyDotProps {
  party: string;
}

export const PartyDot: React.FC<PartyDotProps> = ({ party }) => {
  const p = (party || '').toLowerCase();
  const cls = p.includes('democrat')
    ? 'bg-party-democrat'
    : p.includes('republican')
    ? 'bg-party-republican'
    : p.includes('independ')
    ? 'bg-party-independent'
    : 'bg-neutral-500';

  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} />;
};

// ─── StatusPill ─────────────────────────────────────────────────────────────

interface StatusPillProps {
  status: string;
}

export const StatusPill: React.FC<StatusPillProps> = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cls =
    s === 'active' ? 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300 border-success-200 dark:border-success-700'
    : s === 'on ballot' ? 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300 border-success-200 dark:border-success-700'
    : s === 'filed' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700'
    : s === 'declared' ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300 border-warning-200 dark:border-warning-700'
    : s === 'withdrawn' ? 'bg-neutral-100 dark:bg-background-dark-tertiary text-neutral-600 dark:text-text-dark-tertiary border-neutral-200 dark:border-border-dark'
    : s === 'won' ? 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300 border-success-200 dark:border-success-700'
    : s === 'lost' ? 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300 border-error-200 dark:border-error-700'
    : 'bg-neutral-100 dark:bg-background-dark-tertiary text-neutral-600 dark:text-text-dark-tertiary border-neutral-200 dark:border-border-dark';

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`}>
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current" />
      {status}
    </span>
  );
};

// ─── SectionTitle ───────────────────────────────────────────────────────────

interface SectionTitleProps {
  kicker?: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}

export const SectionTitle: React.FC<SectionTitleProps> = ({ kicker, children, right }) => (
  <div className="flex items-end justify-between mb-1 gap-4">
    <div>
      {kicker && (
        <div className="text-[11px] font-medium tracking-[0.14em] uppercase text-accent-500 dark:text-text-dark-tertiary mb-1">
          {kicker}
        </div>
      )}
      <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary leading-tight">
        {children}
      </h2>
    </div>
    {right}
  </div>
);

// ─── PersonCard (styled wrapper) ────────────────────────────────────────────

interface PersonCardProps {
  children: React.ReactNode;
  className?: string;
}

export const PersonCard: React.FC<PersonCardProps> = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-background-dark-secondary rounded-xl border border-border-light dark:border-border-dark hover:shadow-card-hover dark:hover:shadow-dark-card-hover transition-shadow duration-150 ${className}`}>
    {children}
  </div>
);
